import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { API, whatsappLink, validateSpanishPhone } from "@/lib/faro";
import { trackClick } from "@/lib/analytics";
import { PUNTOS_EUR } from "@/lib/courses";

const PLAZA_AMERICA_WA = "34637570075";

const COURSE_TYPES = [
  { value: "NO_SABE", label: "No estoy seguro — quiero que me orienten" },
  { value: "A", label: `Tipo A — Curso parcial (1 día + 3 h online): recupera hasta 4 puntos · ${PUNTOS_EUR}` },
  { value: "B1", label: "Tipo B1 — Curso total (20 h): pérdida de vigencia por agotar los puntos" },
  { value: "B2", label: "Tipo B2 — Curso total (20 h): retirada judicial de hasta 2 años" },
  { value: "B3", label: "Tipo B3 — Curso total (20 h): retirada judicial de más de 2 años" },
  { value: "CICLO_ADICIONAL", label: "Ciclo adicional — 4 horas (tras suspender la prueba)" },
];

const CARD_META = {
  B1: { title: "Me he quedado sin puntos", sub: "Pérdida de vigencia: curso de 20 h + examen en Tráfico (Tipo B1)" },
  B2: { title: "Retirada judicial de hasta 2 años", sub: "Sentencia: solo el curso de 20 h, sin examen (Tipo B2)" },
  B3: { title: "Retirada judicial de más de 2 años", sub: "Sentencia: curso de 20 h + pruebas de la DGT (Tipo B3)" },
  NO_SABE: { title: "No lo sé", sub: "Prefiero que me llaméis y me orientéis sin compromiso" },
};

const PROVINCES_BY_CP = {
  "01": "Álava", "02": "Albacete", "03": "Alicante", "04": "Almería", "05": "Ávila",
  "06": "Badajoz", "07": "Illes Balears", "08": "Barcelona", "09": "Burgos", "10": "Cáceres",
  "11": "Cádiz", "12": "Castellón", "13": "Ciudad Real", "14": "Córdoba", "15": "A Coruña",
  "16": "Cuenca", "17": "Girona", "18": "Granada", "19": "Guadalajara", "20": "Gipuzkoa",
  "21": "Huelva", "22": "Huesca", "23": "Jaén", "24": "León", "25": "Lleida",
  "26": "La Rioja", "27": "Lugo", "28": "Madrid", "29": "Málaga", "30": "Murcia",
  "31": "Navarra", "32": "Ourense", "33": "Asturias", "34": "Palencia", "35": "Las Palmas",
  "36": "Pontevedra", "37": "Salamanca", "38": "Santa Cruz de Tenerife", "39": "Cantabria", "40": "Segovia",
  "41": "Sevilla", "42": "Soria", "43": "Tarragona", "44": "Teruel", "45": "Toledo",
  "46": "Valencia", "47": "Valladolid", "48": "Bizkaia", "49": "Zamora", "50": "Zaragoza",
  "51": "Ceuta", "52": "Melilla",
};

const DNI_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";

function validateDniNie(raw) {
  const v = (raw || "").toUpperCase().replace(/[\s.-]/g, "");
  const dni = /^(\d{8})([A-Z])$/.exec(v);
  if (dni) return DNI_LETTERS[parseInt(dni[1], 10) % 23] === dni[2];
  const nie = /^([XYZ])(\d{7})([A-Z])$/.exec(v);
  if (nie) {
    const num = parseInt({ X: "0", Y: "1", Z: "2" }[nie[1]] + nie[2], 10);
    return DNI_LETTERS[num % 23] === nie[3];
  }
  return false;
}

function formatDateInput(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function nextSixMonths() {
  const now = new Date();
  const out = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    out.push({ value: `${y}-${m}`, label: `${MONTH_NAMES[d.getMonth()]} ${y}` });
  }
  return out;
}

const EMPTY = {
  course_type: "", course_month: "",
  full_name: "", dni_nie: "", phone: "", no_whatsapp: false, address: "", postal_code: "", city: "", province: "", birth_date: "",
  first_license_date: "", email: "", has_heavy_license: "", comments: "", filled_by_school: false, school_center: "",
};

// Centros donde puede rellenar la secretaría el formulario.
const SCHOOL_CENTERS = [
  { id: "coruxo", name: "Coruxo" },
  { id: "panxon", name: "Panxón" },
  { id: "nigran", name: "Nigrán" },
  { id: "acaniza", name: "A Cañiza" },
  { id: "plazaamerica", name: "Vigo · Plaza América" },
  { id: "andres", name: "ANDRES" },
];

const INPUT_CLS =
  "mt-1 w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-red)] focus:ring-2 focus:ring-[rgba(230,57,70,0.1)] transition bg-white";
const LABEL_CLS = "text-xs uppercase tracking-[0.12em] font-bold text-[var(--faro-blue)]";

function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function cardCls(v, selected) {
  const base = "relative flex items-start gap-3 rounded-2xl p-4 min-h-[48px] cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-[var(--faro-blue)] focus-within:ring-offset-2";
  if (selected) return `${base} border-2 border-[var(--faro-red)] bg-[rgba(230,57,70,0.06)]`;
  if (v === "NO_SABE") return `${base} border-2 border-dashed border-[rgba(30,42,143,0.25)] bg-[var(--faro-bg)] hover:border-[var(--faro-blue)]`;
  return `${base} border-2 border-[rgba(30,42,143,0.15)] bg-white hover:border-[var(--faro-blue)]`;
}

const FormSidebar = ({ sideTitle }) => (
  <div className="lg:col-span-4 bg-[var(--faro-red)] text-white p-8 flex flex-col justify-between">
    <div>
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/80">Inscripción</div>
      <h3 className="font-display font-black text-3xl mt-2 leading-tight">{sideTitle}</h3>
      <p className="text-white/85 mt-3 text-sm">
        Déjanos tu nombre y tu teléfono y tu plaza queda solicitada en menos de un minuto.
        Después podrás completar la matrícula o dejar que te llamemos nosotros.
      </p>
    </div>
    <div className="mt-6 text-sm text-white/90">
      <div className="font-bold">Plaza de América · Vigo</div>
      <div className="text-white/75">Centro autorizado DGT</div>
    </div>
  </div>
);

const CourseTypeSelector = ({ cardOptions, courseType, onChange }) =>
  cardOptions ? (
    <fieldset className="sm:col-span-2 border-0 p-0 m-0">
      <legend className={LABEL_CLS}>¿Qué necesitas? *</legend>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="puntos-course-cards">
        {cardOptions.map((v) => {
          const meta = CARD_META[v];
          const selected = courseType === v;
          return (
            <label key={v} className={cardCls(v, selected)} data-testid={`puntos-card-${v}`}>
              <input type="radio" name="course_type" value={v} checked={selected} onChange={onChange} className="sr-only" data-testid={`puntos-radio-${v}`} />
              <span aria-hidden="true" className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${selected ? "border-[var(--faro-red)]" : "border-[rgba(30,42,143,0.3)]"}`}>
                {selected && <span className="w-2 h-2 rounded-full bg-[var(--faro-red)]"></span>}
              </span>
              <span>
                <span className="block font-bold text-sm text-[var(--faro-blue-deep)] leading-snug">{meta.title}</span>
                <span className="block text-xs text-[var(--faro-text-soft)] mt-0.5 leading-snug">{meta.sub}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  ) : (
    <label className="block sm:col-span-2">
      <span className={LABEL_CLS}>¿Qué necesitas? *</span>
      <select name="course_type" value={courseType} onChange={onChange} data-testid="puntos-course-type" className={INPUT_CLS}>
        {COURSE_TYPES.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
    </label>
  );

const MonthField = ({ form, months, slot, onChange }) => (
  <label className="block sm:col-span-2">
    <span className={LABEL_CLS}>Mes en que quiero hacer el curso *</span>
    <select name="course_month" value={form.course_month} onChange={onChange} data-testid="puntos-course-month" className={INPUT_CLS}>
      <option value="">Selecciona el mes...</option>
      {months.map((m) => (
        <option key={m.value} value={m.value}>{m.label}</option>
      ))}
    </select>
    {slot && (
      <div data-testid="puntos-course-slot" className="mt-2 rounded-xl bg-[var(--faro-blue)] text-white p-3 flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--faro-yellow)] whitespace-nowrap">Fecha del curso</span>
        <span className="text-sm font-semibold">
          Comienza el <b>{fmtDate(slot.start)}</b> — preséntate a las <b>14:50</b> (el curso es de 15:00 a 22:30)
        </span>
      </div>
    )}
    {form.course_month && !slot && (
      <div className="mt-2 rounded-xl bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] p-3 text-xs font-semibold">
        {form.course_type === "CICLO_ADICIONAL"
          ? "El ciclo adicional es individual y se programa de un día para otro. Te llamaremos para concretar la fecha."
          : "Fechas por confirmar para ese mes. Te llamaremos para concretar."}
      </div>
    )}
  </label>
);

const Step2Fields = ({ form, months, slot, dniStatus, onChange, onDateChange, onCpChange }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <MonthField form={form} months={months} slot={slot} onChange={onChange} />
    <label className="block">
      <span className={LABEL_CLS}>DNI o NIE *</span>
      <input
        name="dni_nie" value={form.dni_nie} onChange={onChange} data-testid="puntos-dni"
        className={`${INPUT_CLS} ${dniStatus === "valid" ? "!border-green-500 !ring-2 !ring-green-100 bg-green-50" : ""} ${dniStatus === "invalid" ? "!border-red-500 !ring-2 !ring-red-100" : ""}`}
        placeholder="12345678A"
      />
      {dniStatus === "invalid" && (
        <span data-testid="puntos-dni-error" className="mt-1 block text-xs font-semibold text-red-600">El DNI/NIE no es válido. Revisa el número y la letra.</span>
      )}
      {dniStatus === "valid" && (
        <span data-testid="puntos-dni-ok" className="mt-1 block text-xs font-semibold text-green-600">DNI/NIE válido ✓</span>
      )}
    </label>
    <label className="block">
      <span className={LABEL_CLS}>Fecha de nacimiento *</span>
      <input type="text" inputMode="numeric" name="birth_date" value={form.birth_date} onChange={onDateChange} data-testid="puntos-birth" className={INPUT_CLS} placeholder="DD/MM/AAAA" autoComplete="bday" />
    </label>
    <label className="block sm:col-span-2">
      <span className={LABEL_CLS}>Dirección *</span>
      <input name="address" value={form.address} onChange={onChange} data-testid="puntos-address" className={INPUT_CLS} placeholder="Calle y número" />
    </label>
    <label className="block">
      <span className={LABEL_CLS}>Código postal *</span>
      <input name="postal_code" value={form.postal_code} onChange={onCpChange} inputMode="numeric" data-testid="puntos-cp" className={INPUT_CLS} placeholder="36330" />
    </label>
    <label className="block">
      <span className={LABEL_CLS}>Localidad *</span>
      <input name="city" value={form.city} onChange={onChange} data-testid="puntos-city" className={INPUT_CLS} placeholder="Se rellena con el CP" />
    </label>
    <label className="block">
      <span className={LABEL_CLS}>Provincia *</span>
      <input name="province" value={form.province} onChange={onChange} data-testid="puntos-province" className={INPUT_CLS} placeholder="Se rellena con el CP" />
    </label>
    <label className="block">
      <span className={LABEL_CLS}>Fecha del primer carnet</span>
      <input type="text" inputMode="numeric" name="first_license_date" value={form.first_license_date} onChange={onDateChange} data-testid="puntos-first-license" className={INPUT_CLS} placeholder="DD/MM/AAAA" />
    </label>
    <label className="block sm:col-span-2">
      <span className={LABEL_CLS}>Email</span>
      <input type="email" name="email" value={form.email} onChange={onChange} data-testid="puntos-email" className={INPUT_CLS} placeholder="tunombre@email.com" />
    </label>
    <label className="block sm:col-span-2">
      <span className={LABEL_CLS}>¿Tienes carnet de camión o autobús? Los conductores profesionales pueden quedar exentos de IVA (te lo confirmamos al matricularte)</span>
      <select name="has_heavy_license" value={form.has_heavy_license} onChange={onChange} data-testid="puntos-heavy" className={INPUT_CLS}>
        <option value="">Selecciona...</option>
        <option value="si">Sí</option>
        <option value="no">No</option>
      </select>
    </label>
    <label className="block sm:col-span-2">
      <span className={LABEL_CLS}>Comentarios (opcional)</span>
      <textarea name="comments" value={form.comments} onChange={onChange} rows={4} data-testid="puntos-comments" className={`${INPUT_CLS} resize-y min-h-[96px]`} placeholder="¿Quieres añadir algo? (preferencia horaria, dudas, casos especiales…)" />
    </label>
  </div>
);

export const PointsRegistrationForm = ({ defaultCourseType = "NO_SABE", sideTitle = "Reserva tu curso de puntos", cardOptions = null, contextType = null, formId = "puntos-formulario" }) => {
  const [form, setForm] = useState({ ...EMPTY, course_type: cardOptions ? "" : defaultCourseType });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState(1);
  const [regId, setRegId] = useState(null);
  const [done, setDone] = useState(false);
  const [schedule, setSchedule] = useState({});
  const months = React.useMemo(() => nextSixMonths(), []);

  useEffect(() => {
    axios.get(`${API}/courses/schedule`).then((r) => setSchedule(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (contextType && contextType.v) setForm((f) => ({ ...f, course_type: contextType.v }));
  }, [contextType]);

  useEffect(() => {
    const onSelect = (e) => {
      const v = e && e.detail;
      if (v) setForm((f) => ({ ...f, course_type: v }));
    };
    window.addEventListener("faro:select-course", onSelect);
    return () => window.removeEventListener("faro:select-course", onSelect);
  }, []);

  const isOrientation = form.course_type === "NO_SABE";

  const slot = React.useMemo(() => {
    if (!form.course_month) return null;
    const arr = schedule[form.course_type] || [];
    return arr.find((s) => s.start && s.start.startsWith(form.course_month)) || null;
  }, [schedule, form.course_type, form.course_month]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onDateChange = (e) => setForm({ ...form, [e.target.name]: formatDateInput(e.target.value) });

  const dniClean = form.dni_nie.toUpperCase().replace(/[\s.-]/g, "");
  const dniStatus = dniClean.length === 0 ? "empty" : dniClean.length >= 9 ? (validateDniNie(dniClean) ? "valid" : "invalid") : "typing";

  const onCpChange = (e) => {
    const cp = e.target.value.replace(/\D/g, "").slice(0, 5);
    setForm((f) => ({ ...f, postal_code: cp }));
    if (cp.length !== 5) return;
    const province = PROVINCES_BY_CP[cp.slice(0, 2)] || "";
    setForm((f) => ({ ...f, province: province || f.province }));
    fetch(`https://api.zippopotam.us/es/${cp}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const places = data?.places || [];
        const clean = places.find((p) => !p["place name"].includes("("));
        const city = ((clean || places[0])?.["place name"] || "").replace(/\s*\(.*\)$/, "");
        if (city) setForm((f) => ({ ...f, city }));
      })
      .catch(() => {});
  };

  // PASO 1: el lead se registra YA, aunque abandone el paso 2
  const submitStep1 = async (e) => {
    e.preventDefault();
    if (!form.course_type.trim() || !form.full_name.trim() || !form.phone.trim()) {
      toast.error("Completa todos los campos obligatorios (*)");
      return;
    }
    const phoneCheck = validateSpanishPhone(form.phone);
    if (!phoneCheck.ok) {
      toast.error(phoneCheck.error);
      return;
    }
    if (!consent) {
      toast.error("Debes aceptar la política de privacidad para enviar la solicitud.");
      return;
    }
    if (form.filled_by_school && !form.school_center) {
      toast.error("Selecciona el centro desde el que se está cubriendo el formulario.");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/points-registration`, {
        course_type: form.course_type,
        full_name: form.full_name,
        phone: form.phone,
        no_whatsapp: !!form.no_whatsapp,
        privacy_consent: true,
        partial: true,
        filled_by_school: !!form.filled_by_school,
        school_center: form.filled_by_school ? (form.school_center || "") : "",
        source_page: window.location.pathname,
      });
      setRegId(r.data.id);
      setStep(2);
      trackClick("form_submit_puntos", { center: "plazaamerica", extra: { course_type: form.course_type, step: 1 } });
      toast.success("¡Ya tenemos tu solicitud!");
    } catch {
      toast.error("No hemos podido registrar tu solicitud. Llámanos al 986 29 24 55 o sigue por WhatsApp para no perder tu plaza.");
      const courseLabel = COURSE_TYPES.find((c) => c.value === form.course_type)?.label || form.course_type;
      window.location.href = whatsappLink(PLAZA_AMERICA_WA, `Hola, quiero reservar plaza en un curso de puntos.\nCurso: ${courseLabel}\nNombre: ${form.full_name}\nTeléfono: ${form.phone}`);
    } finally {
      setSending(false);
    }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    const required = ["course_month", "dni_nie", "address", "postal_code", "city", "province", "birth_date"];
    const missing = required.filter((k) => !form[k].trim());
    if (missing.length) {
      toast.error("Completa todos los campos obligatorios (*)");
      return;
    }
    if (!validateDniNie(form.dni_nie)) {
      toast.error("El DNI/NIE introducido no es válido. Revisa el número y la letra.");
      return;
    }
    setSending(true);
    const courseLabel = COURSE_TYPES.find((c) => c.value === form.course_type)?.label || form.course_type;
    const monthLabel = months.find((m) => m.value === form.course_month)?.label || form.course_month;
    try {
      await axios.patch(`${API}/points-registration/${regId}/complete`, {
        course_month: form.course_month,
        dni_nie: form.dni_nie,
        address: form.address,
        postal_code: form.postal_code,
        city: form.city,
        province: form.province,
        birth_date: form.birth_date,
        first_license_date: form.first_license_date,
        email: form.email,
        has_heavy_license: form.has_heavy_license,
        comments: form.comments,
      });
      trackClick("form_complete_puntos", { center: "plazaamerica", extra: { course_type: form.course_type, step: 2 } });
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "reserva_curso", { tipo_curso: form.course_type, rellenado_por_centro: form.filled_by_school ? "si" : "no" });
      }
      toast.success("Matrícula completada. Redirigiendo a WhatsApp...");
      window.location.href = whatsappLink(
        PLAZA_AMERICA_WA,
        `Hola, acabo de completar mi matrícula del curso de puntos.\nCurso: ${courseLabel}\nMes: ${monthLabel}\nNombre: ${form.full_name}\nDNI/NIE: ${form.dni_nie}\nTeléfono: ${form.phone}`
      );
    } catch {
      toast.error("No hemos podido guardar los datos, pero tu solicitud del paso 1 está registrada. Llámanos al 986 29 24 55.");
    } finally {
      setSending(false);
    }
  };

  const preferCall = () => {
    setDone(true);
    trackClick("form_puntos_prefer_call", { center: "plazaamerica", extra: { course_type: form.course_type } });
  };

  const inputCls = INPUT_CLS;
  const labelCls = LABEL_CLS;
  // Error inline del teléfono: solo mostrar cuando ya escribió algo
  const phoneError = form.phone.trim() ? (validateSpanishPhone(form.phone).error || "") : "";

  return (
    <Reveal delay={0.1}>
      <div className="mt-12 rounded-3xl border border-[rgba(30,42,143,0.12)] bg-[var(--faro-bg)] overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <FormSidebar sideTitle={sideTitle} />

          <div className="lg:col-span-8 p-6 sm:p-8" data-testid="puntos-form">
            {done ? (
              <div className="py-10 text-center" data-testid="puntos-done">
                <div className="font-display font-black text-2xl text-[var(--faro-blue-deep)]">¡Listo, {form.full_name.split(" ")[0]}!</div>
                <p className="mt-3 text-[var(--faro-text-soft)]">
                  Tu solicitud está registrada. Te llamaremos al <strong>{form.phone}</strong> para completar la matrícula por teléfono.
                  Si lo prefieres, llámanos tú al <a href="tel:+34986292455" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">986 29 24 55</a>.
                </p>
              </div>
            ) : step === 1 ? (
              <form onSubmit={submitStep1} id={formId} className="space-y-4" data-testid="puntos-form-step1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <CourseTypeSelector cardOptions={cardOptions} courseType={form.course_type} onChange={onChange} />
                  <label className="block sm:col-span-2">
                    <span className={labelCls}>Nombre y apellidos *</span>
                    <input name="full_name" value={form.full_name} onChange={onChange} data-testid="puntos-full-name" className={inputCls} placeholder="Nombre y apellidos" />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className={labelCls}>Teléfono *</span>
                    <input
                      name="phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      maxLength={20}
                      value={form.phone}
                      onChange={onChange}
                      data-testid="puntos-phone"
                      aria-invalid={phoneError ? "true" : "false"}
                      aria-describedby={phoneError ? "puntos-phone-err" : undefined}
                      className={`${inputCls} ${phoneError ? "!border-[var(--faro-red)] focus:!border-[var(--faro-red)] focus:!ring-[rgba(230,57,70,0.15)]" : ""}`}
                      placeholder="6XX XXX XXX"
                    />
                    {phoneError && (
                      <p id="puntos-phone-err" data-testid="puntos-phone-error" className="mt-1 text-xs font-semibold text-[var(--faro-red)]">
                        {phoneError}
                      </p>
                    )}
                    <label className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-[var(--faro-text-soft)] cursor-pointer select-none">
                      <input
                        type="checkbox"
                        name="no_whatsapp"
                        checked={!!form.no_whatsapp}
                        onChange={(e) => setForm({ ...form, no_whatsapp: e.target.checked })}
                        data-testid="puntos-no-whatsapp"
                        className="accent-[var(--faro-red)]"
                      />
                      <span className="uppercase tracking-wider">No tengo WhatsApp</span>
                      <span className="normal-case font-normal text-[var(--faro-text-soft)]">— te llamaremos o enviaremos SMS.</span>
                    </label>
                  </label>
                </div>

                <label className="flex items-start gap-2 text-sm text-[var(--faro-text-soft)] cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="puntos-consent" className="mt-1 accent-[var(--faro-red)]" />
                  <span>
                    He leído y acepto la{" "}
                    <Link to="/politica-privacidad" target="_blank" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">política de privacidad</Link>.
                    Mis datos serán tratados por Automovilística Faro S.L. para gestionar mi reserva y contactar conmigo (también por WhatsApp). *
                  </span>
                </label>

                <label className="flex items-center gap-2 rounded-xl border border-dashed border-[rgba(30,42,143,0.25)] bg-[var(--faro-bg)] px-3 py-2 text-sm text-[var(--faro-text-soft)] cursor-pointer" data-testid="puntos-filled-by-school-wrap">
                  <input
                    type="checkbox"
                    name="filled_by_school"
                    checked={!!form.filled_by_school}
                    onChange={(e) => setForm({ ...form, filled_by_school: e.target.checked, school_center: e.target.checked ? form.school_center : "" })}
                    data-testid="puntos-filled-by-school"
                    className="accent-[var(--faro-blue)]"
                  />
                  <span className="font-semibold text-[var(--faro-blue)]">Cubierto por la autoescuela</span>
                  <span className="text-xs text-[var(--faro-text-soft)]">— uso interno, marca esta casilla si el formulario lo rellena la secretaría en nombre del alumno.</span>
                </label>

                {form.filled_by_school && (
                  <label className="block" data-testid="puntos-school-center-wrap">
                    <span className={labelCls}>Centro que cubre el formulario *</span>
                    <select
                      name="school_center"
                      value={form.school_center || ""}
                      onChange={onChange}
                      data-testid="puntos-school-center"
                      required
                      className={inputCls}
                    >
                      <option value="">— Selecciona un centro —</option>
                      {SCHOOL_CENTERS.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </label>
                )}

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
                  <p className="text-xs text-[var(--faro-text-soft)]">Los campos con * son obligatorios.</p>
                  <button type="submit" disabled={sending} data-testid="puntos-submit" className="btn-pill btn-primary disabled:opacity-60">
                    {sending ? "Enviando..." : isOrientation && cardOptions ? "Que me llamen y me orienten" : "Quiero mi plaza"} <Send size={16} />
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={submitStep2} className="space-y-4" data-testid="puntos-form-step2">
                <div className="rounded-xl bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] p-3 text-sm font-semibold" data-testid="puntos-step2-msg">
                  Ya tenemos tu solicitud. Completa estos datos y dejamos tu plaza RESERVADA hoy mismo
                  (o llámanos al <a href="tel:+34986292455" className="underline underline-offset-2 font-bold">986 29 24 55</a>).
                </div>
                <Step2Fields form={form} months={months} slot={slot} dniStatus={dniStatus} onChange={onChange} onDateChange={onDateChange} onCpChange={onCpChange} />

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
                  <button type="button" onClick={preferCall} data-testid="puntos-prefer-call" className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--faro-blue)] underline underline-offset-2 hover:text-[var(--faro-red)]">
                    <Phone size={14} /> Prefiero que me llamen y lo completo por teléfono
                  </button>
                  <button type="submit" disabled={sending} data-testid="puntos-submit-step2" className="btn-pill btn-primary disabled:opacity-60">
                    {sending ? "Enviando..." : "Completar mi matrícula"} <Send size={16} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </Reveal>
  );
};

export default PointsRegistrationForm;
